import moment from 'moment-timezone';
import type { TagVariant } from '../components/tag';
import {
    Doc31CouponSelectionStatus,
    Doc31EditionStatus,
    Doc31ValidationIssue,
} from '../types/doc31';

/** The 31DOC page title, in its top bar. */
export const DOC31_PAGE_TITLE = '31 Days of Circle K';

/** Doors in a calendar whose edition leaves Play days empty. */
export const DOC31_DEFAULT_PLAY_DAYS = 31;

/** The most doors the API lets an edition's calendar hold. */
export const DOC31_MAX_PLAY_DAYS = 366;

export interface Doc31PlayDays {
    /** First and last local dates of play, `YYYY-MM-DD`. */
    first: string;
    last: string;
    /** Doors the window needs: one per local date. */
    count: number;
    /** Today's door, from 1, while play runs; null before or after it. */
    door: number | null;
    phase: 'upcoming' | 'running' | 'ended';
}

/**
 * The play window in doors, the rule the game applies: one door per local
 * date, the last being the date of the instant before `playEndsAt`, so an end
 * at midnight adds no door. Null for a window that ends before it starts.
 */
export function doc31PlayDays(
    window: { playStartsAt: string; playEndsAt: string; timezone: string },
    now: Date
): Doc31PlayDays | null {
    const first = moment
        .tz(window.playStartsAt, window.timezone)
        .startOf('day');
    const last = moment
        .tz(new Date(window.playEndsAt).getTime() - 1, window.timezone)
        .startOf('day');
    if (!first.isValid() || !last.isValid() || last.isBefore(first)) {
        return null;
    }
    const count = Math.round(last.diff(first, 'days', true)) + 1;
    const elapsed = Math.round(
        moment.tz(now, window.timezone).startOf('day').diff(first, 'days', true)
    );
    const phase =
        elapsed < 0 ? 'upcoming' : elapsed >= count ? 'ended' : 'running';
    return {
        first: first.format('YYYY-MM-DD'),
        last: last.format('YYYY-MM-DD'),
        count,
        door: phase === 'running' ? elapsed + 1 : null,
        phase,
    };
}

/**
 * Play days for a new play window: its door count, so the field follows the
 * dates. The saved value back when the window needs just as many (empty stays
 * empty), and undefined when the window has no doors or more than the API
 * allows, which the header reports.
 */
export function doc31PlayDaysForWindow(
    window: { playStartsAt: string; playEndsAt: string; timezone: string },
    savedPlayDays: number | null
): number | null | undefined {
    const days = doc31PlayDays(window, new Date());
    if (!days || days.count > DOC31_MAX_PLAY_DAYS) {
        return undefined;
    }
    return days.count === (savedPlayDays ?? DOC31_DEFAULT_PLAY_DAYS)
        ? savedPlayDays
        : days.count;
}

export const DOC31_STATUS_TAGS: Record<Doc31EditionStatus, TagVariant> = {
    draft: 'grey',
    scheduled: 'blue',
    active: 'success',
    paused: 'gold',
    earning_closed: 'orange',
    closed: 'grey',
    archived: 'grey',
};

export const DOC31_STATUS_LABELS: Record<Doc31EditionStatus, string> = {
    draft: 'Draft',
    scheduled: 'Scheduled',
    active: 'Live',
    paused: 'Paused',
    earning_closed: 'Earning closed',
    closed: 'Closed',
    archived: 'Archived',
};

export interface Doc31Transition {
    target: Doc31EditionStatus;
    /** The button, the reason prompt and the toast all use this wording. */
    label: string;
    done: string;
    /** Exposes the edition to players: the go-live check must pass first. */
    needsValidSetup: boolean;
    primary: boolean;
}

/** The moves the API allows from each state (`PUT /31doc/state`). */
export const DOC31_TRANSITIONS: Record<
    Doc31EditionStatus,
    Doc31Transition[]
> = {
    draft: [
        {
            target: 'scheduled',
            label: 'Schedule',
            done: 'Scheduled',
            needsValidSetup: true,
            primary: false,
        },
        {
            target: 'active',
            label: 'Go live',
            done: 'Live',
            needsValidSetup: true,
            primary: true,
        },
    ],
    scheduled: [
        {
            target: 'draft',
            label: 'Back to draft',
            done: 'Back in draft',
            needsValidSetup: false,
            primary: false,
        },
        {
            target: 'active',
            label: 'Go live',
            done: 'Live',
            needsValidSetup: true,
            primary: true,
        },
    ],
    active: [
        {
            target: 'earning_closed',
            label: 'Close earning',
            done: 'Earning closed',
            needsValidSetup: false,
            primary: false,
        },
        {
            target: 'paused',
            label: 'Pause play',
            done: 'Play paused',
            needsValidSetup: false,
            primary: true,
        },
    ],
    paused: [
        {
            target: 'earning_closed',
            label: 'Close earning',
            done: 'Earning closed',
            needsValidSetup: false,
            primary: false,
        },
        {
            target: 'active',
            label: 'Resume play',
            done: 'Play resumed',
            // The API refuses only what stops play, as for a live save.
            needsValidSetup: false,
            primary: true,
        },
    ],
    earning_closed: [
        {
            target: 'closed',
            label: 'Close edition',
            done: 'Edition closed',
            needsValidSetup: false,
            primary: true,
        },
    ],
    closed: [
        {
            target: 'archived',
            label: 'Archive',
            done: 'Archived',
            needsValidSetup: false,
            primary: true,
        },
    ],
    archived: [],
};

const EDITION_FIELD_LABELS: Record<string, string> = {
    playStartsAt: 'Play starts',
    playEndsAt: 'Play ends',
    earningStartsAt: 'Earning starts',
    earningEndsAt: 'Earning ends',
    redemptionStartsAt: 'Redemption starts',
    redemptionEndsAt: 'Redemption ends',
    legalVersion: 'Rules version',
};

const RULE_FIELD_LABELS: Record<string, string> = {
    baseEntriesPerPlay: 'Entries per door',
    maxPlaysPerLocalDate: 'Doors per day',
    instantWinProbability: 'Win chance',
    dailyRedemptionLimit: 'Redemptions per day',
    referralClaimWindowHours: 'Referral claim window',
    minimumAge: 'Minimum age',
    enabledCapabilities: 'Capabilities',
    maxPlayDays: 'Play days',
};

const ITEM_FIELD_LABELS: Record<string, string> = {
    title: 'title',
    description: 'description',
    iconUrl: 'icon',
    entries: 'entries',
    isOneTime: 'once per edition',
    maxPerUser: 'limit per player',
    maxPerDay: 'limit per day',
    sortOrder: 'order',
    enabled: 'on/off',
    threshold: 'days to play',
    startDate: 'start',
    endDate: 'end',
    isActive: 'active',
    activationCode: 'activation code',
    quantity: 'quantity',
    categoryId: 'category',
    promotionalOfferId: 'offer',
    provinceIds: 'provinces',
    items: 'copy',
    discountValue: 'discount',
    rebateAmount: 'rebate',
    originalPrice: 'original price',
};

const SELECTION_FIELD_LABELS: Record<string, string> = {
    status: 'draw status',
    weight: 'odds weight',
    pacingTargets: 'pacing',
    maxWinsPerParticipation: 'wins per player',
    minimumAge: 'minimum age',
    alertUtilizationPercent: 'stock alert',
    servingUtilizationPercent: 'serving limit',
};

const LANGUAGE_LABELS: Record<string, string> = { en: 'English', fr: 'French' };

/** Until an edition loads, dates show in the campaign's own timezone. */
export const DOC31_DEFAULT_TIMEZONE = 'America/Toronto';

export const DOC31_COUPON_STATUS_LABELS: Record<
    Doc31CouponSelectionStatus,
    string
> = {
    draft: 'Draft',
    active: 'In the draw',
    withdrawn: 'Withdrawn',
};

function describeField(field: string, language?: string): string {
    const label = ITEM_FIELD_LABELS[field] ?? field;
    return language && LANGUAGE_LABELS[language]
        ? `${LANGUAGE_LABELS[language]} ${label}`
        : label;
}

function describeCouponField(field: string, subField?: string): string {
    if (field === 'selection' && subField) {
        return SELECTION_FIELD_LABELS[subField] ?? subField;
    }
    if (field === 'items' && subField && LANGUAGE_LABELS[subField]) {
        return `${LANGUAGE_LABELS[subField]} copy`;
    }
    return describeField(field);
}

/**
 * An API field path in the operator's words: `rules.instantWinProbability`
 * reads "Win chance", `bonuses.checkout.title.fr` reads "Bonus checkout ·
 * French title", `couponPromotions.14.selection.weight` reads "Coupon 14 ·
 * odds weight", `title` reads "title".
 */
export function describeDoc31Path(path: string): string {
    const [section, ...rest] = path.split('.');
    if (section === 'edition') {
        return EDITION_FIELD_LABELS[rest[0]] ?? rest.join('.');
    }
    if (section === 'rules') {
        return RULE_FIELD_LABELS[rest[0]] ?? rest.join('.');
    }
    if (section === 'bonuses' || section === 'badges') {
        const [key, field, language] = rest;
        const noun = section === 'bonuses' ? 'Bonus' : 'Badge';
        return [`${noun} ${key}`, field && describeField(field, language)]
            .filter(Boolean)
            .join(' · ');
    }
    if (section === 'couponPromotions') {
        const [id, field, subField] = rest;
        if (!id) {
            return 'Coupons';
        }
        return [`Coupon ${id}`, field && describeCouponField(field, subField)]
            .filter(Boolean)
            .join(' · ');
    }
    return describeCouponField(section, rest[0]);
}

/** DOM id of the Setup field a validation path points to. */
export function doc31FieldId(path: string): string {
    return `doc31-${path.replace(/[^a-zA-Z0-9_-]/g, '-')}`;
}

/** The coupon a `couponPromotions.<id>…` path points to, if any. */
export function doc31CouponIdOf(path: string): number | null {
    const match = /^couponPromotions\.(\d+)/.exec(path);
    return match ? Number(match[1]) : null;
}

export const DOC31_ACTION_LABELS: Record<string, string> = {
    'configuration.saved': 'Saved setup',
    'edition.transitioned': 'Changed state',
    'coupon.created': 'Created coupon',
    'coupon.updated': 'Changed coupon',
    'coupon.withdrawn': 'Withdrew coupon',
};

const TYPOGRAPHY: [RegExp, string][] = [
    [/[\u2018\u2019\u201a\u2032]/g, "'"],
    [/[\u201c\u201d\u201e\u2033]/g, '"'],
    [/[\u2013\u2014]/g, '-'],
    [/\u2026/g, '...'],
    [/\u0153/g, 'oe'],
    [/\u0152/g, 'OE'],
    [/\u20ac/g, 'EUR'],
];

/**
 * The reason travels in `X-Command-Reason`, and the browser sends header
 * values as Latin-1: axios drops anything else (line breaks, ’, œ, emoji)
 * without a word. Typography folds to plain equivalents and whitespace to
 * single spaces; what still can't travel is returned so the prompt can ask
 * for it to go.
 */
export function toDoc31HeaderReason(
    text: string
): {
    reason: string;
    unsupported: string[];
} {
    const reason = TYPOGRAPHY.reduce(
        (folded, [pattern, plain]) => folded.replace(pattern, plain),
        text
    )
        .replace(/\s+/g, ' ')
        .trim();
    const unsupported = [
        ...new Set(
            Array.from(reason).filter(
                (char) => (char.codePointAt(0) ?? 0) > 0xff
            )
        ),
    ];
    return { reason, unsupported };
}

/** Setup-check wording by API code; the field name is shown above it. */
const DOC31_ISSUE_TEXT: Record<string, string> = {
    required: 'Required.',
    unsupported_value: '31DOC opens one door per day.',
    out_of_range: 'Must be at least 1.',
    play_window_length: 'Play lasts longer than Play days allows.',
    draft_legal_version: 'Set the final rules version.',
    no_entries: 'Turned on but gives no entries.',
    no_active_coupon: 'No coupon is in the draw.',
    missing_text: 'English or French text is missing.',
    pacing_over_quantity: 'Pacing aims for more wins than the stock.',
    ends_before_play: 'Ends before play starts.',
    window_order: 'Starts after it ends.',
};

/** A setup check in operator words; the API's own text for a new code. */
export function describeDoc31Issue(issue: Doc31ValidationIssue): string {
    return DOC31_ISSUE_TEXT[issue.code] ?? issue.message;
}
